import { Component, computed, inject, input, output, signal } from '@angular/core';
import { AddendumService } from '../../services/addendum.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { TaxAuthorityService, TaxCategoryDto, TaxRateDto, TaxRatesService } from '@zayan-erp/home-building';
import { FormsModule } from '@angular/forms';
import { AddendumOptionTax } from '../../models/addendum-state.models';
import { AbilityService, DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';

@Component({
  selector: 'lib-deal-tax-information',
  imports: [GenericDropdownComponent, FormsModule],
  templateUrl: './deal-tax-information.component.html'
})
export class DealTaxInformationComponent {
  #addendumService = inject(AddendumService);
  #taxRateService = inject(TaxRatesService);
  #taxAuthorityService = inject(TaxAuthorityService);
  #abilityService = inject(AbilityService);
  /** Single source of truth for this component's write gate. */
  canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  addendumProductId = input<string>('');
  addendumProductTaxes = input<AddendumOptionTax[]>([]);
  category = input<TaxCategoryDto | null>(null);
  reload = output<void>();
  buyerAndBuilderApproved = input<boolean>(false);
  isCancelled = input.required<boolean>();

  addAuthority = signal<boolean>(false);
  selectedAuthority = signal<string | null>(null);
  tempTaxRate = signal<TaxRateDto | null>(null);

  taxAuthorities = toSignal(this.#taxAuthorityService.readTaxAuthorities(), { initialValue: [] });
  taxAuthoritiesList = computed<DropdownData[]>(() =>
    this.taxAuthorities().map(authority => ({
      id: authority.id,
      firstLabel: authority.name,
      secondLabel: authority.code
    }))
  );
  addNewTaxAuthority() {
    if (!this.canWriteDeals()) return;
    if (this.selectedAuthority()) {
      this.#addendumService
        .createAddendumProductTax({
          addendumProductId: this.addendumProductId(),
          taxAuthorityId: this.selectedAuthority()!
        })
        .subscribe({
          next: () => {
            this.reload.emit();
            this.addAuthority.set(false);
            this.tempTaxRate.set(null);
            this.selectedAuthority.set(null);
          }
        });
    }
  }

  getTaxRate() {
    if (this.selectedAuthority()) {
      this.#taxRateService.readTaxRates(this.selectedAuthority()!, this.category()?.id!).subscribe({
        next: taxRate => {
          ///this logic is used because most recent tax rate is applied also we need to avoid future tax because those are not implemented yet
          const now = new Date();
          const validTaxRates = taxRate
            .filter(rate => new Date(rate.effectiveDate) <= now)
            .sort((a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime());
          this.tempTaxRate.set(validTaxRates[0] || null);
        }
      });
    }
  }
  deleteTaxAuthority(id: string) {
    if (!this.canWriteDeals()) return;
    this.#addendumService.deleteAddendumProductTax(this.addendumProductId(), id).subscribe({
      next: () => {
        this.reload.emit();
      }
    });
  }

  getTaxRatesForAuthority(event: any) {
    this.#addendumService.getTaxRatesForAuthority(this.addendumProductId(), event).subscribe({
      next: taxRates => {
        this.tempTaxRate.set({
          ...this.tempTaxRate()!,
          ...taxRates
        });
      }
    });
  }
}
